import { WalkthroughConfig } from '../components/common/Walkthrough';
import { useTranslation } from '../i18n/hooks/useTranslation';

// Helper to get translations (will be called when walkthrough starts)
const getT = () => {
  // This will be set dynamically when walkthrough starts
  return (key: string, fallback?: string) => {
    // Try to get from window if available
    const t = (window as any).__t__;
    if (t) return t(key);
    return fallback || key;
  };
};

export const createProjectWalkthrough: () => WalkthroughConfig = () => {
  const t = getT();
  
  return {
    id: 'create-project',
    name: 'Create a Project',
    steps: [
      {
        id: 'step-1',
        targetSelector: '[data-walkthrough="projects-add-button"]',
        title: 'Start Creating a Project',
        description: 'Click the "Add New" button to start creating a new project.',
        position: 'bottom',
        waitForClick: true,
        onBeforeStep: async () => {
          // Navigate to projects page if not already there
          const currentPath = window.location.pathname;
          if (currentPath !== '/projects') {
            window.dispatchEvent(new CustomEvent('navigate', { detail: { path: '/projects' } }));
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        },
      },
      {
        id: 'step-2',
        targetSelector: '[data-walkthrough="project-client-select"]',
        title: 'Select a Client',
        description: 'Choose the client for this project from the dropdown. If the client doesn\'t exist, you can create a new one.',
        position: 'right',
        waitForClick: true,
        onBeforeStep: async () => {
          // Wait for form to be visible
          await new Promise(resolve => setTimeout(resolve, 500));
        },
      },
      {
        id: 'step-3',
        targetSelector: '[data-walkthrough="project-name-input"]',
        title: 'Enter Project Name',
        description: 'Type a descriptive name for your project. This will help you identify it later.',
        position: 'right',
        waitForClick: false,
      },
      {
        id: 'step-4',
        targetSelector: '[data-walkthrough="project-start-date"]',
        title: 'Set Start Date',
        description: 'Select the project start date. This helps track project timelines.',
        position: 'right',
        waitForClick: false,
      },
      {
        id: 'step-5',
        targetSelector: '[data-walkthrough="project-site-address"]',
        title: 'Add Site Address',
        description: 'Enter the project site address. You can use the autocomplete to quickly find addresses.',
        position: 'right',
        waitForClick: false,
      },
      {
        id: 'step-6',
        targetSelector: '[data-walkthrough="project-description"]',
        title: 'Add Description',
        description: 'Optionally add a description or notes about the project. You can use voice input or AI formatting.',
        position: 'right',
        waitForClick: false,
      },
      {
        id: 'step-7',
        targetSelector: '[data-walkthrough="project-save-button"]',
        title: 'Save the Project',
        description: 'Click "Create Project" to save your new project. You\'ll be able to add timesheets, documents, and more after creation.',
        position: 'top',
        waitForClick: true,
      },
    ],
    onComplete: () => {
      console.log('Create Project walkthrough completed');
    },
    onSkip: () => {
      console.log('Create Project walkthrough skipped');
    },
  };
};

export const createTimesheetWalkthrough: () => WalkthroughConfig = () => {
  const t = getT();
  
  return {
    id: 'create-timesheet',
    name: 'Create a Timesheet',
    steps: [
      {
        id: 'step-1',
        targetSelector: '[data-walkthrough="timesheets-add-button"]',
        title: 'Start Creating a Timesheet',
        description: 'Click the "Add Timesheet" button to create a new timesheet entry.',
        position: 'bottom',
        waitForClick: true,
        onBeforeStep: async () => {
          // Navigate to timesheets page if not already there
          const currentPath = window.location.pathname;
          if (currentPath !== '/timesheets') {
            window.dispatchEvent(new CustomEvent('navigate', { detail: { path: '/timesheets' } }));
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        },
      },
      {
        id: 'step-2',
        targetSelector: '[data-walkthrough="timesheet-date-selector"]',
        title: 'Select Date',
        description: 'Choose the date for this timesheet entry. You can navigate between pay periods using the arrows.',
        position: 'bottom',
        waitForClick: true,
        onBeforeStep: async () => {
          // Wait for form to be visible
          await new Promise(resolve => setTimeout(resolve, 500));
        },
      },
      {
        id: 'step-3',
        targetSelector: '[data-walkthrough="timesheet-staff-select"]',
        title: 'Select Staff Member',
        description: 'Choose which staff member this timesheet is for. If you have permission, you can create timesheets for others.',
        position: 'right',
        waitForClick: true,
      },
      {
        id: 'step-4',
        targetSelector: '[data-walkthrough="timesheet-project-select"]',
        title: 'Select Project',
        description: 'Choose the project this timesheet entry is associated with. This links the time to a specific project.',
        position: 'right',
        waitForClick: true,
      },
      {
        id: 'step-5',
        targetSelector: '[data-walkthrough="timesheet-task-select"]',
        title: 'Select Task',
        description: 'Select the task or activity being performed. Tasks help categorize the work being done.',
        position: 'right',
        waitForClick: true,
      },
      {
        id: 'step-6',
        targetSelector: '[data-walkthrough="timesheet-hours-input"]',
        title: 'Enter Hours',
        description: 'Enter the number of hours worked. You can add multiple time entries for the same day.',
        position: 'right',
        waitForClick: false,
      },
      {
        id: 'step-7',
        targetSelector: '[data-walkthrough="timesheet-save-button"]',
        title: 'Save Timesheet',
        description: 'Click "Save" to create the timesheet entry. The entry will be saved and can be approved later.',
        position: 'top',
        waitForClick: true,
      },
    ],
    onComplete: () => {
      console.log('Create Timesheet walkthrough completed');
    },
    onSkip: () => {
      console.log('Create Timesheet walkthrough skipped');
    },
  };
};

export const addStaffWalkthrough: () => WalkthroughConfig = () => {
  const t = getT();
  
  return {
    id: 'add-staff',
    name: 'Add Staff Member',
    steps: [
      {
        id: 'step-1',
        targetSelector: '[data-walkthrough="staff-add-button"]',
        title: 'Start Adding Staff',
        description: 'Click the "Add New" button to add a new staff member to your account.',
        position: 'bottom',
        waitForClick: true,
        onBeforeStep: async () => {
          // Navigate to staff page if not already there
          const currentPath = window.location.pathname;
          if (currentPath !== '/staff') {
            window.dispatchEvent(new CustomEvent('navigate', { detail: { path: '/staff' } }));
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        },
      },
      {
        id: 'step-2',
        targetSelector: '[data-walkthrough="staff-first-name"]',
        title: 'Enter First Name',
        description: 'Type the staff member\'s first name. This is a required field.',
        position: 'right',
        waitForClick: false,
        onBeforeStep: async () => {
          // Wait for form to be visible
          await new Promise(resolve => setTimeout(resolve, 500));
        },
      },
      {
        id: 'step-3',
        targetSelector: '[data-walkthrough="staff-last-name"]',
        title: 'Enter Last Name',
        description: 'Type the staff member\'s last name. This is also required.',
        position: 'right',
        waitForClick: false,
      },
      {
        id: 'step-4',
        targetSelector: '[data-walkthrough="staff-email"]',
        title: 'Enter Email (Optional)',
        description: 'Optionally add the staff member\'s email address. This can be used for invitations and notifications.',
        position: 'right',
        waitForClick: false,
      },
      {
        id: 'step-5',
        targetSelector: '[data-walkthrough="staff-phone"]',
        title: 'Enter Phone (Optional)',
        description: 'Optionally add a phone number for the staff member.',
        position: 'right',
        waitForClick: false,
      },
      {
        id: 'step-6',
        targetSelector: '[data-walkthrough="staff-save-button"]',
        title: 'Save Staff Member',
        description: 'Click "Add Staff" to save the new staff member. They will appear in your staff list immediately.',
        position: 'top',
        waitForClick: true,
      },
    ],
    onComplete: () => {
      console.log('Add Staff walkthrough completed');
    },
    onSkip: () => {
      console.log('Add Staff walkthrough skipped');
    },
  };
};

// Walkthrough registry
export const walkthroughRegistry: Record<string, () => WalkthroughConfig> = {
  'create-project': createProjectWalkthrough,
  'create-timesheet': createTimesheetWalkthrough,
  'add-staff': addStaffWalkthrough,
};

export const getWalkthrough = (id: string): WalkthroughConfig | null => {
  const factory = walkthroughRegistry[id];
  if (!factory) return null;
  return factory();
};

